// components/ExpenseHeader.js
import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const HeaderContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    // backgroundColor: '#2C5283',
    background: `linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)`,
    color: '#fff',
    borderRadius: theme.spacing(1),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
}));

const TitleSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

const StatsSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',
}));

const StatBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: theme.spacing(1.5, 3),
    borderRadius: theme.spacing(1),
    backdropFilter: 'blur(5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
}));

const AddButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    '&:hover': {
        backgroundColor: theme.palette.success.dark,
    },
    borderRadius: '8px',
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
}));

const ExpenseHeader = ({ totalExpenses, totalAmount, onAddExpense, isMobile }) => {
    return (
        <HeaderContainer>
            <TitleSection>
                <ReceiptLongIcon sx={{ fontSize: 40 }} />
                <Box>
                    <Typography variant="h5" fontWeight="600">
                        Expense Management
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        Track and manage your property expenses
                    </Typography>
                </Box>
            </TitleSection>

            {!isMobile && (
                <StatsSection>
                    <StatBox>
                        <ReceiptLongIcon sx={{ fontSize: 28 }} />
                        <Box>
                            <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                Total Expenses
                            </Typography>
                            <Typography variant="h6" fontWeight="600">
                                {totalExpenses}
                            </Typography>
                        </Box>
                    </StatBox>

                    <StatBox>
                        <AttachMoneyIcon sx={{ fontSize: 28 }} />
                        <Box>
                            <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                Total Amount
                            </Typography>
                            <Typography variant="h6" fontWeight="600">
                                {totalAmount.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'KSH',
                                })}
                            </Typography>
                        </Box>
                    </StatBox>

                    <AddButton
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={onAddExpense}
                    >
                        Add Expense
                    </AddButton>
                </StatsSection>
            )}

            {isMobile && (
                <Button
                    variant="contained"
                    color="success"
                    onClick={onAddExpense}
                    sx={{ minWidth: 'unset', width: 40, height: 40, borderRadius: '50%', p: 0 }}
                >
                    <AddIcon />
                </Button>
            )}
        </HeaderContainer>
    );
};

ExpenseHeader.propTypes = {
    totalExpenses: PropTypes.number.isRequired,
    totalAmount: PropTypes.number.isRequired,
    onAddExpense: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired,
};

export default ExpenseHeader;